import React, {useCallback, useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Tag} from "primereact/tag";

import {SellerIdentityService} from "../../service/SellerIdentityService";
import {SellerWorkspaceService} from "../../service/SellerWorkspaceService";
import {formatTime} from "./sellerFormat";

const sellerWorkspaceService = new SellerWorkspaceService();
const sellerIdentityService = new SellerIdentityService();
const POLL_MS = 20000;
const DEFAULT_SCOPE = 'demo';
// The list step always reads every seller, so it has no scope.
const STEP_LIST = 'koctas_seller_list';

const row = {display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap'};
const muted = {color: '#6b7280'};

const stepLabel = (step, t) => ({
    koctas_seller_list: t('sellers_workspace.steps.list', 'Koçtaş satıcılarını çek'),
    seller_store_match: t('sellers_workspace.steps.match', "Trendyol ve Hepsiburada'da bul ve eşleştir"),
    seller_store_count: t('sellers_workspace.steps.count', 'Ürün sayılarını çek'),
}[step] || step);

const stateText = (state, stuck, t) => {
    if (stuck) return t('sellers_workspace.steps.stuck', 'Yarım kaldı');
    if (state === 'done') return t('sellers_workspace.steps.done', 'Bitti');
    if (state === 'failed') return t('sellers_workspace.steps.failed', 'Hata');
    return state ? t('sellers_workspace.steps.running', 'Çalışıyor') : null;
};

const sumOf = (counts) => Object.values(counts).reduce((total, value) => total + value, 0);

const StepResult = ({t, step}) => {
    const result = step.result || {};
    return <span style={muted}>
        {result.written !== undefined && t('sellers_workspace.steps.written', '{{count}} satıcı yazıldı', {count: result.written})}
        {result.findings && t('sellers_workspace.steps.compared', '{{sellers}} satıcı: {{exact}} eşleşti, {{partial}} kısmi, {{different}} farklı, {{unreadable}} okunamadı',
                              {sellers: result.sellers, ...result.findings})}
        {result.searched > 0 && <span style={{marginLeft: 6}}>
            {t('sellers_workspace.steps.searched', '{{searched}} satıcı arandı: {{found}} bulundu, {{not_found}} bulunamadı', result)}
        </span>}
        {result.search_failed > 0 && <Tag severity="warning" style={{marginLeft: 6}}
                                          value={t('sellers_workspace.steps.searchFailed', '{{count}} arama başarısız', {count: result.search_failed})}/>}
        {result.failed_sellers > 0 && <Tag severity="warning" style={{marginLeft: 6}}
                                           value={t('sellers_workspace.steps.failedSellers', '{{count}} satıcı okunamadı', {count: result.failed_sellers})}/>}
        {result.read && t('sellers_workspace.steps.counted', '{{month}}: Koçtaş {{koctas}}, Trendyol {{trendyol}}, Hepsiburada {{hepsiburada}}',
                          {month: result.month, ...result.read})}
        {result.failed && sumOf(result.failed) > 0 &&
            <Tag severity="warning" style={{marginLeft: 6}} value={t('sellers_workspace.steps.countFailed', '{{count}} okunamadı', {count: sumOf(result.failed)})}/>}
        {step.error && <Tag severity="danger" style={{marginLeft: 6}} value={step.error}/>}
        {result.partial && <Tag severity="warning" style={{marginLeft: 6}}
                                value={t('sellers_workspace.steps.partial', 'Eksik: {{count}} sorgu başarısız', {count: result.failed_queries ?? 0})}/>}
    </span>;
};

// Admin only: the flow in order, each step with its last run. Steps run as background jobs, so the
// panel only starts them and watches. Nothing here runs on a schedule.
const SellerSteps = ({t, toast, onError, onMatched}) => {
    const [steps, setSteps] = useState([]);
    const [starting, setStarting] = useState(null);
    const [matching, setMatching] = useState(false);
    const [scopes, setScopes] = useState({});
    const load = useCallback(() => sellerWorkspaceService.getSteps().then(setSteps).catch(onError), [onError]);
    useEffect(() => { load(); }, [load]);

    const anyRunning = steps.some(step => !step.can_run);
    useEffect(() => {
        if (!anyRunning) return undefined;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [anyRunning, load]);

    const run = (step) => {
        setStarting(step.step);
        sellerWorkspaceService.runStep(step.step, scopes[step.step] || DEFAULT_SCOPE)
            .catch(onError).then(load).finally(() => setStarting(null));
    };
    const runMatching = () => {
        setMatching(true);
        sellerIdentityService.refresh()
            .then((result) => {
                toast.current?.show({severity: 'success', summary: t('onboarding.sites.identity.runMatching', 'Eşleştirmeyi çalıştır'),
                                     detail: t('onboarding.sites.identity.matchingResult', '{{auto}} otomatik eşleşme, {{suggested}} yeni öneri',
                                               {auto: result?.auto_added ?? 0, suggested: result?.suggested_added ?? 0})});
                return onMatched();
            })
            .catch(onError).finally(() => setMatching(false));
    };

    return <div style={{border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 14px', marginBottom: 16}}>
        <div style={{...row, justifyContent: 'space-between'}}>
            <strong>{t('sellers_workspace.steps.title', 'Yönetici adımları')}</strong>
            <Button icon="pi pi-refresh" className="p-button-text p-button-sm" aria-label={t('sellers_workspace.steps.reload', 'Yenile')} onClick={load}/>
        </div>
        {steps.map((step, index) => (
            <div key={step.step} style={row}>
                <span style={{flex: '1 1 18em'}}>{index + 1}. {stepLabel(step.step, t)}</span>
                {!step.available && <Tag severity="info" value={t('sellers_workspace.steps.soon', 'Yakında')}/>}
                {stateText(step.state, step.stuck, t) && <span>{stateText(step.state, step.stuck, t)}</span>}
                <span style={muted}>{formatTime(step.last_run_at)}</span>
                <StepResult t={t} step={step}/>
                {step.available && step.step !== STEP_LIST &&
                    <Dropdown value={scopes[step.step] || DEFAULT_SCOPE} style={{width: '14em'}}
                              options={[{label: t('sellers_workspace.steps.scopeDemo', 'Kampanyadakiler'), value: DEFAULT_SCOPE},
                                        {label: t('sellers_workspace.steps.scopeAll', 'Tüm liste'), value: 'all'}]}
                              onChange={(e) => setScopes(current => ({...current, [step.step]: e.value}))}/>}
                <Button label={t('sellers_workspace.steps.run', 'Çalıştır')} icon="pi pi-play" className="p-button-sm p-button-outlined seller-step-run"
                        loading={starting === step.step} disabled={!step.available || !step.can_run} onClick={() => run(step)}/>
            </div>))}
        <div style={{...row, borderBottom: 'none'}}>
            <span style={{flex: '1 1 18em'}}>{t('sellers_workspace.steps.matchTracked', 'İzlenen ürünlerden eşleştir')}</span>
            <Button label={t('onboarding.sites.identity.runMatching', 'Eşleştirmeyi çalıştır')} icon="pi pi-sync"
                    className="p-button-outlined p-button-sm" loading={matching} onClick={runMatching}/>
        </div>
    </div>;
};

export default SellerSteps;
